import { getAuthToken } from '../utils/authUtils'; //
import axios from 'axios';

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';


/**
 * Secure Claims Workflow Service
 * Uses backend API endpoints instead of direct Firestore access
 */
class ClaimsWorkflowService {
  constructor() {
    this.batchSize = 100;
    this.retryAttempts = 3;
    this.retryDelay = 5000; // 5 seconds
  }

  /**
   * Initialize the claims workflow with clearinghouse provider
   * @param {string} provider - 'OFFICE_ALLY' or 'OPTUM'
   * @param {Object} credentials - Provider-specific credentials
   */
  async initialize(provider, credentials) {
    try {
      console.log('Initializing claims workflow via API:', provider);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/billing/claims/initialize`,
        {
          provider,
          credentials
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.success;
    } catch (error) {
      console.error('Failed to initialize claims workflow:', error);
      throw error;
    }
  }

  /**
   * Process end-to-end claims workflow from encounter to submission
   * @param {string} encounterId - The encounter ID
   * @param {Object} options - Processing options
   */
  async processClaimFromEncounter(encounterId, options = {}) {
    try {
      console.log('Starting claims workflow for encounter via API:', encounterId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/billing/claims/process-encounter/${encounterId}`,
        options,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return {
        success: true,
        workflowId: response.data.workflowId,
        status: response.data.status,
        message: response.data.message
      };
    } catch (error) {
      console.error('Claims workflow failed:', error);
      if (axios.isAxiosError(error) && error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw error;
    }
  }

  /**
   * Check patient eligibility
   * @param {string} encounterId - The encounter ID
   */
  async checkPatientEligibility(encounterId) {
    try {
      console.log('Checking patient eligibility via API:', encounterId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/billing/eligibility/check`,
        { encounterId },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.eligibilityResult;
    } catch (error) {
      console.error('Eligibility check failed:', error);
      throw error;
    }
  }

  /**
   * Generate claim data from bill
   * @param {Object} bill - The generated bill
   * @param {string} encounterId - The encounter ID
   */
  async generateClaimData(bill, encounterId) {
    try {
      console.log('Generating claim data via API:', bill.id, encounterId);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/billing/claims/generate`,
        {
          billId: bill.id,
          encounterId
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.claimData;
    } catch (error) {
      console.error('Failed to generate claim data:', error);
      throw error;
    }
  }

  /**
   * Generate X12 EDI file
   * @param {Object} claimData - The claim data
   * @param {string} transactionType - The transaction type ('837P', '835', '999')
   */
  async generateEDIFile(claimData, transactionType = '837P') {
    try {
      console.log('Generating EDI file via API:', claimData.claimNumber, transactionType);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/billing/edi/generate`,
        {
          claimData,
          transactionType
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.ediData;
    } catch (error) {
      console.error('Failed to generate EDI file:', error);
      throw error;
    }
  }

  /**
   * Submit claim to clearinghouse
   * @param {Object} claimData - The claim data
   * @param {string} ediData - The EDI file data
   */
  async submitClaim(claimData, ediData) {
    try {
      console.log('Submitting claim via API:', claimData.claimNumber);
      const token = await getAuthToken();
      
      let attempt = 0;
      let lastError = null;
      
      while (attempt < this.retryAttempts) {
        try {
          const response = await axios.post(
            `${API_BASE}/billing/claims/submit`,
            {
              claimData,
              ediData
            },
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            }
          );
          
          return response.data.submissionResult;
          
        } catch (error) {
          lastError = error;
          attempt++;
          
          if (attempt < this.retryAttempts) {
            console.log(`Submission attempt ${attempt} failed, retrying in ${this.retryDelay}ms...`);
            await this.delay(this.retryDelay);
          }
        }
      }
      
      throw new Error(`Claim submission failed after ${this.retryAttempts} attempts: ${lastError.message}`);
    } catch (error) {
      console.error('Claim submission failed:', error);
      throw error;
    }
  }

  /**
   * Update claim status and tracking
   * @param {string} claimNumber - The claim number
   * @param {string} status - The new status
   * @param {Object} additionalData - Additional data to store
   */
  async updateClaimStatus(claimNumber, status, additionalData = {}) {
    try {
      console.log('Updating claim status via API:', claimNumber, status);
      const token = await getAuthToken();
      
      await axios.put(
        `${API_BASE}/billing/claims/${claimNumber}/status`,
        {
          status,
          additionalData
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return { success: true };
    } catch (error) {
      console.error('Failed to update claim status:', error);
      throw error;
    }
  }

  /**
   * Process batch of encounters for billing
   * @param {Array} encounterIds - Array of encounter IDs
   * @param {Object} options - Processing options
   */
  async batchProcessClaims(encounterIds, options = {}) {
    try {
      console.log('Starting batch claims processing via API:', encounterIds.length);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/billing/claims/batch-process`,
        {
          encounterIds,
          options
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return {
        batchId: response.data.batchId,
        status: response.data.status,
        totalEncounters: response.data.totalEncounters,
        message: response.data.message
      };
    } catch (error) {
      console.error('Batch processing failed:', error);
      throw error;
    }
  }

  /**
   * Get batch processing status
   * @param {string} batchId - The batch ID
   */
  async getBatchStatus(batchId) {
    try {
      console.log('Getting batch status via API:', batchId);
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/billing/claims/batch/${batchId}/status`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.batchStatus;
    } catch (error) {
      console.error('Error getting batch status:', error);
      throw error;
    }
  }

  /**
   * Download and process remittance advice
   * @param {Object} dateRange - Date range for remittance download
   */
  async processRemittanceAdvice(dateRange) {
    try {
      console.log('Processing remittance advice via API:', dateRange);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/billing/remittance/process`,
        { dateRange },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.remittanceData;
    } catch (error) {
      console.error('Remittance processing failed:', error);
      throw error;
    }
  }

  /**
   * Process individual remittance record
   * @param {Object} remittance - Remittance data
   */
  async processRemittanceRecord(remittance) {
    try {
      console.log('Processing remittance record via API:', remittance.claimNumber);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/billing/remittance/process-record`,
        { remittance },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.result;
    } catch (error) {
      console.error('Failed to process remittance record:', error);
      throw error;
    }
  }

  /**
   * Check claim statuses
   * @param {Array} claimNumbers - Array of claim numbers to check
   */
  async checkClaimStatuses(claimNumbers) {
    try {
      console.log('Checking claim statuses via API:', claimNumbers.length);
      
      const promises = claimNumbers.map(claimNumber => 
        this.getClaimStatus(claimNumber)
      );
      
      const results = await Promise.allSettled(promises);
      
      return results.map((result, index) => {
        if (result.status === 'fulfilled') {
          return { 
            success: true, 
            claimNumber: claimNumbers[index], 
            status: result.value 
          };
        } else {
          return { 
            success: false, 
            claimNumber: claimNumbers[index], 
            error: result.reason.message 
          };
        }
      });
    } catch (error) {
      console.error('Claim status check failed:', error);
      throw error;
    }
  }

  /**
   * Get claim status
   * @param {string} claimNumber - The claim number
   */
  async getClaimStatus(claimNumber) {
    try {
      console.log('Getting claim status via API:', claimNumber);
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/billing/claims/${claimNumber}/status`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return {
        claim: response.data.claim,
        statusHistory: response.data.statusHistory
      };
    } catch (error) {
      console.error('Error getting claim status:', error);
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        throw new Error('Claim not found');
      }
      throw error;
    }
  }

  /**
   * Get EDI configuration
   */
  async getEDIConfiguration() {
    try {
      console.log('Getting EDI configuration via API');
      const token = await getAuthToken();
      
      const response = await axios.get(
        `${API_BASE}/billing/edi/configuration`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.configuration;
    } catch (error) {
      console.error('Failed to get EDI configuration:', error);
      throw error;
    }
  }

  /**
   * Update EDI configuration
   */
  async updateEDIConfiguration(configuration) {
    try {
      console.log('Updating EDI configuration via API');
      const token = await getAuthToken();
      
      await axios.put(
        `${API_BASE}/billing/edi/configuration`,
        { configuration },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return { success: true };
    } catch (error) {
      console.error('Failed to update EDI configuration:', error);
      throw error;
    }
  }

  /**
   * Get workflow statistics
   */
  async getWorkflowStats(dateRange) {
    try {
      console.log('Getting workflow statistics via API:', dateRange);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      if (dateRange.facilityId) {
        params.append('facilityId', dateRange.facilityId);
      }
      if (dateRange.startDate) {
        params.append('startDate', dateRange.startDate);
      }
      if (dateRange.endDate) {
        params.append('endDate', dateRange.endDate);
      }
      
      const response = await axios.get(
        `${API_BASE}/billing/claims/stats?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.stats;
    } catch (error) {
      console.error('Failed to get workflow stats:', error);
      throw error;
    }
  }

  /**
   * Get claims by status
   */
  async getClaimsByStatus(status, facilityId = null, page = 1, limit = 50) {
    try {
      console.log('Getting claims by status via API:', status);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      params.append('status', status);
      params.append('page', page.toString());
      params.append('limit', limit.toString());
      
      if (facilityId) {
        params.append('facilityId', facilityId);
      }
      
      const response = await axios.get(
        `${API_BASE}/billing/claims?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return {
        claims: response.data.claims,
        pagination: response.data.pagination
      };
    } catch (error) {
      console.error('Error getting claims by status:', error);
      throw error;
    }
  }

  /**
   * Search claims
   */
  async searchClaims(searchTerm, filters = {}) {
    try {
      console.log('Searching claims via API:', searchTerm);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await axios.get(
        `${API_BASE}/billing/claims/search?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.claims;
    } catch (error) {
      console.error('Error searching claims:', error);
      throw error;
    }
  }

  /**
   * Get pending claims
   */
  async getPendingClaims(facilityId = null) {
    try {
      return await this.getClaimsByStatus('pending', facilityId);
    } catch (error) {
      console.error('Error getting pending claims:', error);
      throw error;
    }
  }

  /**
   * Get submitted claims
   */
  async getSubmittedClaims(facilityId = null) {
    try {
      return await this.getClaimsByStatus('submitted', facilityId);
    } catch (error) {
      console.error('Error getting submitted claims:', error);
      throw error;
    }
  }

  /**
   * Get paid claims
   */
  async getPaidClaims(facilityId = null) {
    try {
      return await this.getClaimsByStatus('paid', facilityId);
    } catch (error) {
      console.error('Error getting paid claims:', error);
      throw error;
    }
  }

  /**
   * Get rejected claims
   */
  async getRejectedClaims(facilityId = null) {
    try {
      return await this.getClaimsByStatus('rejected', facilityId);
    } catch (error) {
      console.error('Error getting rejected claims:', error);
      throw error;
    }
  }

  /**
   * Export claims data
   */
  async exportClaims(filters = {}, format = 'json') {
    try {
      console.log('Exporting claims via API:', filters, format);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      params.append('format', format);
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await axios.get(
        `${API_BASE}/billing/claims/export?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (format === 'json') {
        return JSON.stringify(response.data.claims, null, 2);
      } else {
        return response.data.exportData;
      }
    } catch (error) {
      console.error('Error exporting claims:', error);
      throw error;
    }
  }

  /**
   * Utility functions
   */
  generateClaimNumber() {
    const timestamp = Date.now().toString();
    const random = Math.random().toString(36).substr(2, 5).toUpperCase();
    return `CLM${timestamp.slice(-6)}${random}`;
  }

  getPlaceOfService(encounterType) {
    const placeOfServiceMap = {
      'outpatient': '11', // Office
      'inpatient': '21',  // Inpatient Hospital
      'emergency': '23',  // Emergency Room
      'telehealth': '02'  // Telehealth
    };
    return placeOfServiceMap[encounterType] || '11';
  }

  getFacilityType(encounterType) {
    const facilityTypeMap = {
      'outpatient': '11',
      'inpatient': '21',
      'emergency': '23',
      'telehealth': '02'
    };
    return facilityTypeMap[encounterType] || '11';
  }

  chunkArray(array, size) {
    const chunks = [];
    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }
    return chunks;
  }

  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Log activity to the backend
   */
  async logActivity(activityType, data) {
    try {
      const token = await getAuthToken();
      
      await axios.post(
        `${API_BASE}/billing/claims/log-activity`,
        {
          activityType,
          data
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (error) {
      console.error('Failed to log activity:', error);
      // Don't throw - logging failures shouldn't break the workflow
    }
  }

  /**
   * Get workflow logs
   */
  async getWorkflowLogs(filters = {}) {
    try {
      console.log('Getting workflow logs via API:', filters);
      const token = await getAuthToken();
      
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value.toString());
        }
      });
      
      const response = await axios.get(
        `${API_BASE}/billing/claims/logs?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.logs;
    } catch (error) {
      console.error('Error getting workflow logs:', error);
      throw error;
    }
  }

  /**
   * Retry failed claims
   */
  async retryFailedClaims(claimNumbers) {
    try {
      console.log('Retrying failed claims via API:', claimNumbers.length);
      const token = await getAuthToken();
      
      const response = await axios.post(
        `${API_BASE}/billing/claims/retry`,
        { claimNumbers },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      return response.data.retryResults;
    } catch (error) {
      console.error('Error retrying failed claims:', error);
      throw error;
    }
  }
}

/**
 * Get claim details by claim ID
 * @param {string} claimId - The claim ID
 * @returns {Promise<Object>} - The claim details
 */
export async function getClaimDetails(claimId) {
  try {
    console.log('Getting claim details via API:', claimId);
    const token = await getAuthToken();
    
    const response = await axios.get(
      `${API_BASE}/billing/claims/${claimId}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.claim || response.data;
  } catch (error) {
    console.error('Error getting claim details:', error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      throw new Error('Claim not found');
    }
    throw error;
  }
}

export default ClaimsWorkflowService;
